import { useEffect, useRef, useState } from "react";
import Vapi from "@vapi-ai/web";
import { toast } from "sonner";
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageTransition from "@/components/PageTransition";

enum CallStatus {
  INACTIVE = "INACTIVE",
  CONNECTING = "CONNECTING",
  ACTIVE = "ACTIVE",
  FINISHED = "FINISHED",
  ERROR = "ERROR",
}

interface SavedMessage {
  role: "user" | "system" | "assistant";
  content: string;
}

// Vapi configuration
const VAPI_CONFIG = {
  apiKey: "6a57bb74-4a92-4b7c-9138-40071a5bb659",
  assistantId: "4ffc368d-d81c-450a-bb56-f88f2b6d5eb2",
};

export default function VoiceAgent() {
  const vapiRef = useRef<any>(null);
  const [callStatus, setCallStatus] = useState<CallStatus>(CallStatus.INACTIVE);
  const [messages, setMessages] = useState<SavedMessage[]>([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [lastMessage, setLastMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [retryCount, setRetryCount] = useState(0);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);

  const initializeVapi = () => {
    try {
      // Create new Vapi instance
      vapiRef.current = new Vapi(VAPI_CONFIG.apiKey);
      return true;
    } catch (error) {
      console.error("Failed to initialize Vapi:", error);
      return false;
    }
  };

  const cleanupVapi = () => {
    if (vapiRef.current) {
      try {
        vapiRef.current.stop();
      } catch (error) {
        console.error("Error during cleanup:", error);
      }
      vapiRef.current = null;
    }
  };

  useEffect(() => {
    if (!currentUser) {
      toast.error("Please sign in to use the Voice Agent");
      navigate('/auth/signin');
      return;
    }

    // Initialize Vapi
    initializeVapi();

    const onCallStart = () => {
      console.log("Call started");
      setCallStatus(CallStatus.ACTIVE);
      setRetryCount(0); // Reset retry count on successful start
      toast.success("Voice call started");
      // Set output volume to 100% if possible
      if (vapiRef.current && vapiRef.current.audioElement) {
        vapiRef.current.audioElement.volume = 1.0;
      }
      if (vapiRef.current && vapiRef.current.audioContext && vapiRef.current.audioContext.gain) {
        vapiRef.current.audioContext.gain.value = 1.0;
      }
    };

    const onCallEnd = () => {
      console.log("Call ended");
      setCallStatus(CallStatus.FINISHED);
      cleanupVapi(); // Clean up the Vapi instance
      toast.info("Voice call ended");
    };

    const onMessage = (message: any) => {
      if (message.type === "transcript" && message.transcriptType === "final") {
        const newMessage = { role: message.role, content: message.transcript };
        setMessages((prev) => [...prev, newMessage]);
      }
      // Set user speaking state based on message role
      if (message.role === "user") {
        setIsUserSpeaking(true);
        setTimeout(() => setIsUserSpeaking(false), 1000);
      }
    };

    const onSpeechStart = () => {
      console.log("Assistant is speaking");
      setIsSpeaking(true);
    };

    const onSpeechEnd = () => {
      console.log("Assistant finished speaking");
      setIsSpeaking(false);
    };

    const onError = (error: any) => {
      console.error("Vapi error:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isEjectionError = typeof errorMessage === 'string' && 
        (errorMessage.includes("ejection") || errorMessage.includes("Meeting has ended"));
      
      if (isEjectionError) {
        cleanupVapi(); // Clean up the current instance
        
        if (retryCount < 3) {
          setErrorMessage("Connection lost. Attempting to reconnect...");
          setCallStatus(CallStatus.ERROR);
          
          // Wait a moment before retrying
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            initializeVapi(); // Create a new instance
            handleCall(); // Try to start a new call
          }, 2000);
        } else {
          setErrorMessage("Connection failed after multiple attempts. Please try again.");
          setCallStatus(CallStatus.ERROR);
        }
      } else {
        setErrorMessage("An error occurred. Please try again.");
        setCallStatus(CallStatus.ERROR);
      }
    };

    // Set up event listeners
    if (vapiRef.current) {
      vapiRef.current.on("call-start", onCallStart);
      vapiRef.current.on("call-end", onCallEnd);
      vapiRef.current.on("message", onMessage);
      vapiRef.current.on("speech-start", onSpeechStart);
      vapiRef.current.on("speech-end", onSpeechEnd);
      vapiRef.current.on("error", onError);
    }

    // Cleanup
    return () => {
      cleanupVapi();
    };
  }, [currentUser, navigate, retryCount]);

  useEffect(() => {
    if (messages.length > 0) {
      setLastMessage(messages[messages.length - 1].content);
    }
  }, [messages]);

  const handleCall = async () => {
    try {
      setCallStatus(CallStatus.CONNECTING);
      
      // Ensure we have a valid Vapi instance
      if (!vapiRef.current) {
        const initialized = initializeVapi();
        if (!initialized) {
          throw new Error("Failed to initialize voice agent");
        }
      }

      await vapiRef.current.start(
        VAPI_CONFIG.assistantId,
        {
          firstMessageMode: 'assistant-speaks-first'
        }
      );
    } catch (error) {
      console.error("Failed to start call:", error);
      setErrorMessage("Failed to start voice call. Please try again.");
      setCallStatus(CallStatus.ERROR);
      cleanupVapi();
    }
  };

  const handleDisconnect = () => {
    try {
      cleanupVapi();
      setCallStatus(CallStatus.FINISHED);
    } catch (error) {
      console.error("Failed to stop call:", error);
      setErrorMessage("Failed to stop voice call. Please try again.");
      setCallStatus(CallStatus.ERROR);
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    setErrorMessage("");
    setCallStatus(CallStatus.INACTIVE);
    cleanupVapi();
  };

  if (!currentUser) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-slate-900 py-4 px-2 md:px-8 lg:px-16">
      <div className="w-full max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl space-y-8 px-2 md:px-8 lg:px-12">
        <div className="relative z-10">
          <div className="absolute -inset-4 md:-inset-8 lg:-inset-12 bg-gradient-to-br from-truth/30 via-blue-500/10 to-transparent rounded-3xl blur-2xl opacity-60 pointer-events-none" />
          <h1 className="text-3xl font-bold text-truth mb-6 drop-shadow-[0_2px_16px_rgba(0,255,255,0.25)]">Voice Agent: Discuss Misinformation & Privacy</h1>
          <div className="w-full max-w-4xl bg-black/40 rounded-2xl p-6 shadow-2xl border border-truth/30 backdrop-blur-xl glassmorphism-panel">
            {/* Main Mixer Interface */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* AI Channel */}
              <div className="bg-gray-800/60 rounded-2xl p-4 border border-truth/20 shadow-lg glassmorphism-panel">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-truth/20 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🤖</span>
                    </div>
                    <div>
                      <h3 className="text-truth font-bold">AI Assistant</h3>
                      <p className="text-gray-400 text-sm">Channel 1</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${isSpeaking ? 'bg-truth animate-pulse' : 'bg-gray-600'}`}></div>
                    <span className={`${isSpeaking ? 'text-truth' : 'text-gray-500'} text-sm`}>
                      {isSpeaking ? 'Active' : 'Standby'}
                    </span>
                  </div>
                </div>

                {/* AI Level Meter */}
                <div className="mb-4">
                  <div className="h-32 bg-black/60 rounded-lg p-2 flex items-end gap-1 border border-truth/10">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={`ai-meter-${i}`}
                        className={`flex-1 rounded-sm transition-all duration-200 ${
                          isSpeaking ? 'bg-truth' : 'bg-gray-800'
                        }`}
                        style={{
                          height: isSpeaking ? `${Math.random() * 100}%` : '10%',
                          opacity: isSpeaking ? 0.8 : 0.3,
                          boxShadow: isSpeaking ? '0 0 8px rgba(0, 122, 204, 0.3)' : 'none'
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* AI Controls */}
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-2 bg-black/60 rounded-full">
                      <div className="h-full bg-truth rounded-full shadow-[0_0_5px_rgba(0,122,204,0.5)]" style={{ width: '100%' }}></div>
                    </div>
                    <p className="text-gray-400 text-xs mt-1">Output Level</p>
                  </div>
                  <button className="px-3 py-1 bg-truth/20 text-truth rounded hover:bg-truth/30 transition-colors backdrop-blur">
                    Mute
                  </button>
                </div>
              </div>

              {/* User Channel */}
              <div className="bg-gray-800/60 rounded-2xl p-4 border border-blue-400/20 shadow-lg glassmorphism-panel">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <span className="text-2xl">👤</span>
                    </div>
                    <div>
                      <h3 className="text-blue-400 font-bold">User Input</h3>
                      <p className="text-gray-400 text-sm">Channel 2</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${isUserSpeaking ? 'bg-blue-500 animate-pulse' : 'bg-gray-600'}`}></div>
                    <span className={`${isUserSpeaking ? 'text-blue-400' : 'text-gray-500'} text-sm`}>
                      {isUserSpeaking ? 'Active' : 'Standby'}
                    </span>
                  </div>
                </div>

                {/* User Level Meter */}
                <div className="mb-4">
                  <div className="h-32 bg-black/60 rounded-lg p-2 flex items-end gap-1 border border-blue-500/10">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={`user-meter-${i}`}
                        className={`flex-1 rounded-sm transition-all duration-200 ${
                          isUserSpeaking ? 'bg-blue-500' : 'bg-gray-800'
                        }`}
                        style={{
                          height: isUserSpeaking ? `${Math.random() * 100}%` : '10%',
                          opacity: isUserSpeaking ? 0.8 : 0.3,
                          boxShadow: isUserSpeaking ? '0 0 8px rgba(66, 153, 225, 0.3)' : 'none'
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* User Controls */}
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-2 bg-black/60 rounded-full">
                      <div className="h-full bg-blue-500 rounded-full shadow-[0_0_5px_rgba(66,153,225,0.5)]" style={{ width: '75%' }}></div>
                    </div>
                    <p className="text-gray-400 text-xs mt-1">Input Level</p>
                  </div>
                  <button className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors">
                    Mute
                  </button>
                </div>
              </div>
            </div>

            {/* Master Controls */}
            <div className="mt-6 bg-gray-800/60 rounded-2xl p-4 border border-truth/20 shadow-lg glassmorphism-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold">Master Controls</h3>
                <div className="flex items-center gap-4">
                  <span className="text-gray-400 text-sm">Status: {callStatus}</span>
                  <div className="w-3 h-3 rounded-full bg-truth animate-pulse"></div>
                </div>
              </div>

              {/* Call Controls */}
              <div className="flex flex-col items-center gap-4">
                {callStatus === CallStatus.INACTIVE && (
                  <button
                    onClick={handleCall}
                    className="w-full max-w-md bg-truth text-white px-8 py-4 rounded-lg font-bold hover:bg-truth/90 transition-colors flex items-center justify-center gap-2 text-lg shadow-lg hover:shadow-truth/20"
                  >
                    <span className="w-4 h-4 bg-white rounded-full animate-pulse"></span>
                    Start Voice Session
                  </button>
                )}
                
                {callStatus === CallStatus.ACTIVE && (
                  <div className="flex flex-col items-center gap-4">
                    <div className="text-truth text-lg font-medium">Session Active</div>
                    <button
                      onClick={handleDisconnect}
                      className="w-full max-w-md bg-red-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-lg shadow-lg hover:shadow-red-600/20"
                    >
                      <span className="w-4 h-4 bg-white rounded-full"></span>
                      End Session
                    </button>
                  </div>
                )}

                {callStatus === CallStatus.ERROR && (
                  <div className="text-center">
                    <p className="text-red-500 font-medium text-lg mb-2">{errorMessage}</p>
                    <button
                      onClick={handleRetry}
                      className="bg-truth text-white px-8 py-3 rounded-lg font-bold hover:bg-truth/90 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {callStatus === CallStatus.CONNECTING && (
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-truth text-lg font-medium">Connecting...</div>
                    <div className="w-8 h-8 border-4 border-truth border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-4 text-center text-gray-400 text-sm">
              {callStatus === CallStatus.INACTIVE && (
                <p>Click the Start Voice Session button above to begin your conversation</p>
              )}
              {callStatus === CallStatus.ACTIVE && (
                <p>You can now speak with the AI assistant. Click End Session when you're done.</p>
              )}
            </div>

            {/* Transcript Display */}
            {messages.length > 0 && (
              <div className="mt-6 bg-gray-800/60 rounded-2xl p-4 border border-truth/20 shadow-lg glassmorphism-panel">
                <h3 className="text-white font-bold mb-2">Live Transcript</h3>
                <div className="bg-black/30 rounded-lg p-4">
                  <p className="text-gray-200 animate-fadeIn">
                    {lastMessage}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <p className="mt-6 text-gray-400 text-center max-w-xl mx-auto">
          Start a conversation with the AI voice agent about misinformation, hacking, or online privacy. The agent will ask questions, give advice, and help you stay safe and informed online.
        </p>
        {/* Add the animation keyframes and glassmorphism styles to the document */}
        <style>
          {`
            @keyframes sound-wave {
              0% { transform: scaleY(0.5); }
              50% { transform: scaleY(1.5); }
              100% { transform: scaleY(0.5); }
            }
            .animate-sound-wave {
              animation: sound-wave 1s ease-in-out infinite;
            }
            .glassmorphism-panel {
              background: linear-gradient(135deg, rgba(0,255,255,0.08) 0%, rgba(0,0,0,0.5) 100%);
              box-shadow: 0 8px 32px 0 rgba(0,255,255,0.10), 0 1.5px 8px 0 rgba(0,0,0,0.15);
              border: 1.5px solid rgba(0,255,255,0.12);
              backdrop-filter: blur(16px) saturate(120%);
            }
          `}
        </style>
      </div>
    </div>
  );
}
